import ArrowUpIcon from "@redotech/redo-icons/arrow-up_filled.svg";
import PaperclipIcon from "@redotech/redo-icons/paperclip_filled.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import * as aiGlowStyles from "@redotech/redo-web/ai-glow.module.css";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import type { FileUIPart } from "ai";
import classNames from "classnames";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ASK_REDO_ATTACHMENT_ACCEPT,
  AskRedoAttachmentsRow,
  useAttachments,
} from "./ask-redo-attachments";
import { AskRedoPageContextChip } from "./ask-redo-page-context-chip";
import { AskRedoPinnedReferencesRow } from "./ask-redo-pinned-references-row";
import * as styles from "./ask-redo-prompt-input.module.css";

/**
 * Chat input + submit button.
 *
 * Patterned on AI Elements' `<PromptInput>`: auto-expanding textarea,
 * single button that toggles between Send (when there's text) and
 * Stop (while streaming), Enter to send, Shift+Enter for newline.
 *
 * The button stays as a single `IconButton` element across states so
 * the click target's React identity doesn't swap mid-click — a
 * mousedown on Stop while streaming, mouseup after streaming ends,
 * dispatches `click` against the SAME element, which is also when
 * the handler has already become Send. We guard `handleSend` against
 * the `isStreaming === true` race regardless.
 *
 * Attachments: a paperclip button to the left of Send opens a file
 * picker. Selected files upload via `UploaderContext` (provided by
 * `ClientUploader` in the layout). Uploaded files are sent as
 * `FileUIPart[]` on the next message and the row clears.
 */
export interface AskRedoPromptInputProps {
  isStreaming: boolean;
  /** Called with the typed text and any successfully-uploaded
   *  attachments. Empty `files` array when the merchant sent text
   *  alone. */
  /** `files` defaults to an empty array — callers that don't care
   *  about uploads can call `onSend(text)` and ignore the second
   *  argument without breaking. */
  onSend: (text: string, files?: FileUIPart[]) => void;
  onStop: () => void;
  placeholder?: string;
  /**
   * When set, hard-disable the input. Used while an escalation is in
   * the `waiting` state — typing during that window would route to
   * the agent (because human-active hasn't kicked in yet), which
   * re-fires escalate-to-human and creates duplicate Intercom
   * conversations. Mirrors help-chat's `disableChatInput` prop.
   */
  disabled?: boolean;
  /** Override placeholder copy when disabled (e.g., "Waiting for
   *  support to join…"). */
  disabledPlaceholder?: string;
  /**
   * Seed the textarea on mount — used for `prefill` panel options
   * without `autoSubmit` (e.g. "Create with AI" entry points that
   * start the merchant off with "Create an OMS automation: ").
   * Read once; later prop changes don't overwrite what the merchant
   * has typed. Callers that need a fresh seed remount (the slide-out
   * already does, via its `sessionKey`).
   */
  initialValue?: string;
}

export const AskRedoPromptInput = memo(function AskRedoPromptInput({
  isStreaming,
  onSend,
  onStop,
  placeholder,
  disabled,
  disabledPlaceholder,
  initialValue,
}: AskRedoPromptInputProps) {
  const [value, setValue] = useState(initialValue ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    attachments,
    isUploading,
    hasErrors,
    toFileParts,
    addFiles,
    remove,
    clearAll,
    uploaderAvailable,
  } = useAttachments();
  const { clearPins, pinnedReferences, snapshotPinsForNextMessage } =
    useContext(GlobalAgentContext);

  // Focus the textarea whenever a pin is added or changes in place (e.g.
  // the merchant clicks a bubble, or swaps from thumbs up to thumbs
  // down on the same message). Skip when the list only shrinks —
  // removing a pin via the × button shouldn't drag focus back into the
  // chat. Comparing identity is enough because GlobalAgentProvider
  // produces a new array on every pin/unpin.
  const prevPinsRef = useRef(pinnedReferences);
  useEffect(() => {
    const prev = prevPinsRef.current;
    if (pinnedReferences !== prev) {
      const added = pinnedReferences.length > prev.length;
      const changedInPlace =
        pinnedReferences.length > 0 && pinnedReferences.length === prev.length;
      if (added || changedInPlace) {
        textareaRef.current?.focus();
      }
    }
    prevPinsRef.current = pinnedReferences;
  }, [pinnedReferences]);

  const hasUploadedAttachment = attachments.some(
    (a) => a.status === "uploaded",
  );

  // Allow send when:
  //   - not externally disabled (waiting for human escalation)
  //   - not streaming
  //   - no upload in flight
  //   - no errored uploads still in the row
  //   - either text OR at least one uploaded attachment is present
  const canSend =
    !disabled &&
    !isStreaming &&
    !isUploading &&
    !hasErrors &&
    (value.trim().length > 0 || hasUploadedAttachment);

  const handleSend = useCallback(() => {
    if (disabled || isStreaming || isUploading || hasErrors) {
      return;
    }
    const text = value.trim();
    const files = toFileParts();
    if (!text && files.length === 0) {
      return;
    }
    setValue("");
    clearAll();
    // Snapshot the current pins as "pending" so the next user message
    // to land in chat.messages can claim them and render its own chip
    // row above. Must happen BEFORE `onSend` because the conversation
    // view's drain effect may fire as early as the next render after
    // sendMessage appends the user entry — and definitely before pins
    // are cleared in the isStreaming effect below. We pass the pins
    // array explicitly so the snapshot is truly synchronous (no
    // setState callback timing to worry about).
    snapshotPinsForNextMessage(pinnedReferences);
    // NOTE: do NOT clear pins here. The chat lib's `sendMessage` is
    // async — the underlying transport reads `latestPageContext` after
    // our handler returns, by which point a synchronous `clearPins()`
    // would have flushed through React + the publishing useEffect in
    // `use-ask-redo-chat.ts`, wiping the pin's pageData out of the
    // outbound request. Instead, clear pins in the isStreaming effect
    // below — once isStreaming flips true the request has actually
    // dispatched and the pageContext snapshot it carries is locked.
    onSend(text, files);
  }, [
    disabled,
    isStreaming,
    isUploading,
    hasErrors,
    value,
    toFileParts,
    clearAll,
    pinnedReferences,
    snapshotPinsForNextMessage,
    onSend,
  ]);

  // Clear pins one tick after the request fires. `isStreaming` flipping
  // false→true is our signal that the transport already read
  // pageContext (with pins) and put it on the wire. Doing this in an
  // effect (instead of synchronously in handleSend) avoids racing the
  // chat lib's async dispatch — see the comment in handleSend.
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (isStreaming && !wasStreaming.current) {
      clearPins();
    }
    wasStreaming.current = isStreaming;
  }, [isStreaming, clearPins]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Skip Enter while the IME is composing — pressing Enter to
      // commit a Japanese/Chinese/Korean composition would otherwise
      // submit a half-typed message. `nativeEvent.isComposing` is
      // the modern API; `keyCode === 229` covers older browsers
      // that don't expose isComposing for the commit keystroke.
      if (e.nativeEvent.isComposing || e.keyCode === 229) {
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) {
          handleSend();
        }
      }
    },
    [canSend, handleSend],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        addFiles(files);
      }
      // Reset so picking the same file twice in a row still fires
      // the onChange.
      e.target.value = "";
    },
    [addFiles],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const buttonAction = isStreaming ? onStop : handleSend;
  const buttonDisabled = disabled || (!isStreaming && !canSend);

  function AskRedoSendSpinner() {
    return <RedoSpinner size="xs" style="track" />;
  }

  function AskRedoSendIcon() {
    return (
      <ArrowUpIcon
        aria-hidden="true"
        className={classNames(
          styles.sendIcon,
          !canSend && styles.sendIconDisabled,
        )}
      />
    );
  }

  const effectivePlaceholder = disabled
    ? (disabledPlaceholder ?? "Waiting for support to join…")
    : (placeholder ?? "Ask Redo anything…");

  return (
    <div className={classNames(styles.wrapper, aiGlowStyles.aiGlowFocus)}>
      <AskRedoPageContextChip />
      <AskRedoPinnedReferencesRow />
      <AskRedoAttachmentsRow attachments={attachments} onRemove={remove} />
      <RedoTextareaInputField
        aria-label="Message Ask Redo"
        autoExpand
        className={styles.textarea}
        disabled={disabled}
        fullWidth
        onKeyDown={handleKeyDown}
        placeholder={effectivePlaceholder}
        ref={textareaRef}
        setValue={setValue}
        size={RedoTextareaInputFieldSize.REGULAR}
        toolbar={
          <Flex align="center" gap="xs" justify="space-between" w="full">
            {/* design-allow: hidden file picker cannot be a text field */}
            <input
              accept={ASK_REDO_ATTACHMENT_ACCEPT}
              multiple
              onChange={handleFileSelect}
              ref={fileInputRef}
              // design-allow: hidden file input cannot be a text field
              style={{ display: "none" }}
              type="file"
            />
            <RedoButton
              aria-label="Attach file"
              buttonClassName={styles.attachButton}
              disabled={disabled || isStreaming || !uploaderAvailable}
              hierarchy="tertiary"
              IconLeading={PaperclipIcon}
              onClick={(event) => {
                openFilePicker();
                event.stopPropagation();
              }}
              size="sm"
              theme="normal"
            />
            <RedoButton
              aria-label={isStreaming ? "Stop responding" : "Send message"}
              buttonClassName={classNames(
                styles.sendButton,
                isStreaming
                  ? styles.sendStreaming
                  : canSend
                    ? styles.sendEnabled
                    : styles.sendDisabled,
              )}
              disabled={buttonDisabled}
              hierarchy="tertiary"
              IconLeading={isStreaming ? AskRedoSendSpinner : AskRedoSendIcon}
              onClick={(event) => {
                buttonAction();
                event.stopPropagation();
              }}
              size="sm"
              theme="normal"
            />
          </Flex>
        }
        value={value}
      />
    </div>
  );
});
